import { supabase } from "@/integrations/supabase/client";
import type { Practice } from "@/data/practices";

export interface DbPracticeRow {
  id: string;
  name: string;
  type: string;
  nhs_status: "nhs" | "private" | "mixed";
  accepting_new_patients: "yes" | "limited" | "no";
  address: string | null;
  postcode: string;
  borough: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  opening_hours: string | null;
  services: string[] | null;
  languages: string[] | null;
  wheelchair_accessible: boolean;
  private_pay: boolean;
  out_of_area_registrations: boolean;
  restricted: boolean;
  lat: number | null;
  lng: number | null;
  last_updated: string | null;
  source: string;
  active: boolean;
}

export const rowToPractice = (r: DbPracticeRow): Practice & { lat?: number | null; lng?: number | null } => ({
  id: r.id,
  name: r.name,
  type: r.type as Practice["type"],
  nhsStatus: r.nhs_status,
  services: r.services ?? [],
  borough: r.borough ?? "",
  postcode: r.postcode,
  address: r.address ?? "",
  phone: r.phone ?? "",
  email: r.email ?? undefined,
  website: r.website ?? undefined,
  acceptingNewPatients: r.accepting_new_patients,
  description: "",
  openingHours: r.opening_hours ?? "",
  rating: 0,
  reviews: 0,
  distance: 0,
  wheelchairAccessible: r.wheelchair_accessible,
  privatePay: r.private_pay,
  outOfAreaRegistrations: r.out_of_area_registrations,
  languages: r.languages ?? [],
  restricted: r.restricted,
  lastUpdated: r.last_updated ?? undefined,
  lat: r.lat,
  lng: r.lng,
});

/** Page through to fetch ALL rows matching a query (Supabase caps at 1000). */
async function fetchAll(builder: ReturnType<typeof basePracticesQuery>): Promise<DbPracticeRow[]> {
  const out: DbPracticeRow[] = [];
  const PAGE = 1000;
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await builder.range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as DbPracticeRow[];
    out.push(...rows);
    if (rows.length < PAGE) break;
    from += PAGE;
  }
  return out;
}

function basePracticesQuery() {
  return supabase.from("practices").select("*").eq("active", true);
}

export async function fetchPracticeById(id: string) {
  const { data, error } = await supabase
    .from("practices")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data ? rowToPractice(data as unknown as DbPracticeRow) : null;
}

/** Fetch practices by exact postcode (case-insensitive, ignores spaces). */
export async function fetchByPostcode(postcode: string) {
  const norm = postcode.trim().toUpperCase().replace(/\s+/g, "");
  // We stored postcodes in their canonical form (with space). Try both forms.
  const withSpace = norm.length > 3 ? `${norm.slice(0, -3)} ${norm.slice(-3)}` : norm;
  const { data, error } = await supabase
    .from("practices")
    .select("*")
    .eq("active", true)
    .or(`postcode.eq.${withSpace},postcode.eq.${norm}`);
  if (error) throw error;
  return ((data ?? []) as unknown as DbPracticeRow[]).map(rowToPractice);
}

/**
 * Fetch practices within a bounding box around lat/lng (~ radiusMiles).
 * 1 degree latitude ≈ 69 miles. Longitude scales by cos(lat).
 */
export async function fetchNear(lat: number, lng: number, radiusMiles: number) {
  const dLat = radiusMiles / 69;
  const dLng = radiusMiles / (69 * Math.max(0.1, Math.cos((lat * Math.PI) / 180)));
  // Page through to fetch ALL rows in the bbox (Supabase caps a single request at 1000).
  const out: DbPracticeRow[] = [];
  const PAGE = 1000;
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await supabase
      .from("practices")
      .select("*")
      .eq("active", true)
      .gte("lat", lat - dLat)
      .lte("lat", lat + dLat)
      .gte("lng", lng - dLng)
      .lte("lng", lng + dLng)
      .order("id")
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as unknown as DbPracticeRow[];
    out.push(...rows);
    if (rows.length < PAGE) break;
    from += PAGE;
  }
  return out.map(rowToPractice);
}

/** Fetch all practices in a given borough (paged). */
export async function fetchByBorough(borough: string) {
  const builder = supabase
    .from("practices")
    .select("*")
    .eq("active", true)
    .eq("borough", borough)
    .order("name");
  const rows = await fetchAll(builder as unknown as ReturnType<typeof basePracticesQuery>);
  return rows.map(rowToPractice);
}

/** Fetch list of distinct boroughs that have at least one practice. */
export async function fetchBoroughs(): Promise<string[]> {
  const { data, error } = await supabase
    .from("practices")
    .select("borough")
    .eq("active", true)
    .not("borough", "is", null);
  if (error) throw error;
  const set = new Set<string>();
  for (const r of (data ?? []) as { borough: string | null }[]) {
    if (r.borough && r.borough.trim()) set.add(r.borough);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Fetch summary stats (total, accepting %). */
export async function fetchStats() {
  const { count: total } = await supabase
    .from("practices")
    .select("*", { count: "exact", head: true })
    .eq("active", true);
  const { count: accepting } = await supabase
    .from("practices")
    .select("*", { count: "exact", head: true })
    .eq("active", true)
    .in("accepting_new_patients", ["yes", "limited"]);
  const t = total ?? 0;
  const a = accepting ?? 0;
  return { total: t, accepting: a, acceptingPct: t ? Math.round((a / t) * 100) : 0 };
}

/** Lightweight name search for the claim listing autocomplete. */
export async function searchPracticesByName(q: string, limit = 25) {
  const term = q.trim();
  if (!term) return [];
  const { data, error } = await supabase
    .from("practices")
    .select("id,name,postcode,address,type,borough")
    .eq("active", true)
    .ilike("name", `%${term}%`)
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as Array<{
    id: string; name: string; postcode: string; address: string | null;
    type: string; borough: string | null;
  }>;
}

/** Fetch practices by chain/group name (only seed data has chainName, so we filter client side via id list for now). */
export async function fetchByIds(ids: string[]) {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from("practices")
    .select("*")
    .in("id", ids);
  if (error) throw error;
  return ((data ?? []) as unknown as DbPracticeRow[]).map(rowToPractice);
}

/** Return [{name, slug, count}] for all boroughs, sorted by count desc. */
export async function fetchBoroughCounts(): Promise<Array<{ name: string; count: number }>> {
  const { data, error } = await supabase
    .from("practices")
    .select("borough")
    .eq("active", true)
    .not("borough", "is", null)
    .limit(50000);
  if (error) throw error;
  const counts = new Map<string, number>();
  for (const r of (data ?? []) as { borough: string | null }[]) {
    if (!r.borough) continue;
    counts.set(r.borough, (counts.get(r.borough) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}